import type { CSSProperties } from 'react';

/**
 * Значения взяты из восстановленного бандла один в один: объект R, границы,
 * тени, стиль подписей, база кнопок и метрики сетки. Ничего не подгонялось
 * на глаз — если что-то правится, правится и здесь, и в исходнике.
 */

/** Цвета. Всё через CSS-переменные, поэтому темы переключаются сами. */
export const R = {
  bg: 'var(--rt-bg)',
  bgSubtle: 'var(--rt-panel)',
  bgPanel: 'var(--rt-panel)',
  bgElevated: 'var(--rt-elev)',
  bgInput: 'var(--rt-input)',
  border: 'var(--rt-line)',
  grid: 'var(--rt-grid)',
  text: 'var(--rt-fg)',
  textSecondary: 'var(--rt-fg-2)',
  textMuted: 'var(--rt-mut)',
  accent: 'var(--rt-acc)',
  accentSubtle: 'var(--rt-acc-soft)',
  accentBorder: 'var(--rt-acc-line)',
  onAccent: 'var(--rt-on-acc)',
  accentText: 'var(--rt-acc-text)',
  danger: 'var(--rt-danger)',
  dangerSubtle: 'var(--rt-danger-soft)',
  dangerBorder: 'var(--rt-danger-line)',
  success: 'var(--rt-success)',
  successSubtle: 'var(--rt-success-soft)',
  warning: 'var(--rt-warning)',
  warningSubtle: 'var(--rt-warning-soft)',
  selectionSubtle: 'var(--rt-select-soft)',
} as const;

/** Границы. strong — двойная, ей отбиваются крупные блоки. */
export const ft = {
  strong: '2px solid var(--rt-line)',
  hair: '1px solid var(--rt-grid)',
  edge: '1px solid var(--rt-line)',
} as const;

export const shadow = {
  panel: '0 12px 32px rgba(20, 18, 17, .28)',
  dropdown: '0 12px 32px rgba(20, 18, 17, .24)',
} as const;

export const mono = "'JetBrains Mono', ui-monospace, Menlo, monospace";

/** Мелкая подпись-метка: капсом, с разрядкой. */
export const label: CSSProperties = {
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--rt-mut)',
};

export const fieldLabel: CSSProperties = {
  ...label,
  display: 'block',
  marginBottom: 6,
  marginTop: 12,
};

export const input: CSSProperties = {
  width: '100%',
  minHeight: 36,
  padding: '8px 10px',
  fontSize: 14,
  border: ft.edge,
  background: R.bgInput,
  color: R.text,
  boxSizing: 'border-box',
};

const buttonBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 34,
  padding: '11px 15px 10px',
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1,
  cursor: 'pointer',
  border: '1px solid transparent',
};

export const btnPrimary: CSSProperties = {
  ...buttonBase,
  background: R.accent,
  color: R.onAccent,
};

export const btnGhost: CSSProperties = {
  ...buttonBase,
  background: 'transparent',
  color: R.text,
  borderColor: R.border,
};

export const btnDanger: CSSProperties = {
  ...buttonBase,
  background: 'transparent',
  color: R.danger,
  borderColor: R.dangerBorder,
};

/** Цвет аватарки подбирается хешем от строки — так он стабилен для человека. */
const AVATAR_COLORS = [
  '#2f6fd0', '#4a7c59', '#7a4a86', '#8a5a2b',
  '#3d6f70', '#9a3b3b', '#615d5d', '#2c5f8a',
];

export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ---------------- метрики сетки ----------------

export const GUTTER_W = 44;
export const ROW_H = 30;
export const COL_W = 130;
export const HEADER_H = 38;

export const thCell: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  background: R.bgSubtle,
  padding: '0 10px',
  textAlign: 'left',
  fontWeight: 400,
  fontSize: 11,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: R.textMuted,
  borderBottom: ft.strong,
  borderRight: `1px solid ${R.grid}`,
  height: HEADER_H,
  cursor: 'pointer',
  userSelect: 'none',
};

export const thCorner: CSSProperties = {
  ...thCell,
  textAlign: 'center',
  padding: 0,
  background: R.bgSubtle,
  cursor: 'default',
};

export const tdCell: CSSProperties = {
  position: 'relative',
  padding: 0,
  color: R.text,
  background: R.bg,
  borderRight: `1px solid ${R.grid}`,
  borderBottom: `1px solid ${R.grid}`,
  overflow: 'hidden',
};

export const tdGutter: CSSProperties = {
  position: 'relative',
  padding: 0,
  textAlign: 'center',
  fontSize: 11,
  fontFamily: mono,
  color: R.textMuted,
  background: R.bgSubtle,
  borderRight: `1px solid ${R.grid}`,
  borderBottom: `1px solid ${R.grid}`,
  cursor: 'pointer',
  userSelect: 'none',
  verticalAlign: 'middle',
};

/** Подписи типов колонок — тоже из исходника, у меня были свои. */
export const COLUMN_TYPE_LABELS = {
  Any: 'Автоматически',
  Text: 'Текст',
  Numeric: 'Число',
  Int: 'Целое',
  Bool: 'Флажок (галочка)',
  Date: 'Дата',
  DateTime: 'Дата и время',
  Choice: 'Список',
} as const;

/** A1-нотация: 0 → A, 26 → AA. */
export function colLabel(index: number): string {
  let out = '';
  let n = index;
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** Клавиша в подсказке и меню — размеры из исходника. */
export const kbd: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 19,
  height: 19,
  padding: '0 5px',
  fontSize: 10.5,
  lineHeight: 1,
  border: ft.edge,
  color: R.textMuted,
  background: R.bgSubtle,
};

// ---------------- поля формы и кнопки ----------------

/** Поле ввода в модалках. */
export const field: CSSProperties = {
  width: '100%',
  minHeight: 36,
  padding: '8px 10px',
  fontSize: 14,
  border: ft.edge,
  background: R.bgInput,
  color: R.text,
  boxSizing: 'border-box',
};

/** Подпись над полем — та же метка с разрядкой, но блоком. */
export const fieldLabelBlock: CSSProperties = {
  ...label,
  display: 'block',
  marginBottom: 6,
  marginTop: 12,
};

const formButtonBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 34,
  padding: '11px 15px 10px',
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1,
  cursor: 'pointer',
  border: '1px solid transparent',
};

export const btnAccent: CSSProperties = {
  ...formButtonBase,
  background: R.accent,
  color: R.onAccent,
};

export const btnOutline: CSSProperties = {
  ...formButtonBase,
  background: 'transparent',
  color: R.text,
  borderColor: R.border,
};

export const btnDangerSolid: CSSProperties = {
  ...formButtonBase,
  background: R.danger,
  color: R.onAccent,
};

/** Русское склонение по числу: 1 таблица, 2 таблицы, 5 таблиц. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** Чип фильтра по разделу. */
export function chip(active: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    fontSize: 12,
    cursor: 'pointer',
    border: `1px solid ${active ? R.accentBorder : R.border}`,
    background: active ? R.accentSubtle : 'transparent',
    color: active ? R.accent : R.textSecondary,
    whiteSpace: 'nowrap',
  };
}

/** Цвет уровня доступа в легенде и подписях. */
export function levelTone(level: 'owner' | 'editor' | 'viewer' | 'none') {
  switch (level) {
    case 'owner':
      return { bg: R.accentSubtle, fg: R.accent, line: R.accentBorder };
    case 'editor':
      return { bg: 'transparent', fg: R.text, line: R.border };
    case 'viewer':
      return { bg: 'transparent', fg: R.textSecondary, line: R.grid };
    default:
      return { bg: 'transparent', fg: R.textMuted, line: R.grid };
  }
}
